import { useEffect, useState } from 'react'
import { X, AtSign, List, Layout, Calendar, Plus, ChevronRight, ChevronDown, BarChart2, FileText, LogOut, MessageSquarePlus, History, Settings, Search } from 'lucide-react'
import { MobileMenuItem } from './MobileMenuItem'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import { Button } from './ui/Button'
import { useUser } from '../contexts/UserContext'
import { Avatar } from './ui/Avatar'
import { motion, AnimatePresence } from 'framer-motion'
import { UserMenu } from './UserMenu'

interface MobileMenuProps {
    isOpen: boolean
    onClose: () => void
    onChatAction?: (action: 'new' | 'history' | 'settings') => void
}

export function MobileMenu({ isOpen, onClose, onChatAction }: MobileMenuProps) {
    useBodyScrollLock(isOpen)
    const [isVisualizationOpen, setIsVisualizationOpen] = useState(false)
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
    const { user, logout } = useUser()

    // Close menu on ESC key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose()
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [isOpen, onClose])

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Overlay - Backdrop Blur */}
                        <motion.div
                            {...({
                                initial: { opacity: 0 },
                                animate: { opacity: 1 },
                                exit: { opacity: 0 }
                            } as any)}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-40"
                            onClick={onClose}
                        />

                        {/* Bottom Sheet Container */}
                        <motion.div
                            {...({
                                initial: { y: "100%" },
                                animate: { y: 0 },
                                exit: { y: "100%" },
                                drag: "y",
                                dragConstraints: { top: 0 },
                                dragElastic: { top: 0, bottom: 0.2 }
                            } as any)}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            onDragEnd={(_event: any, info: any) => {
                                if (info.offset.y > 100) {
                                    onClose()
                                }
                            }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-black rounded-t-[24px] overflow-hidden max-h-[85vh] flex flex-col shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.3)]"
                            role="dialog"
                            aria-modal="true"
                        >
                            {/* Drag Handle Area (Non-scrollable) */}
                            <div className="w-full flex justify-center pt-3 pb-4 shrink-0 cursor-grab active:cursor-grabbing">
                                <div className="w-12 h-1.5 bg-gray-300 dark:bg-zinc-700 rounded-full" />
                            </div>

                            {/* Scrollable Content */}
                            <div className="flex-1 overflow-y-auto overflow-x-hidden pb-8">

                                {/* User Header Card */}
                                <div className="px-4 pt-2 pb-6">
                                    <button
                                        onClick={() => setIsUserMenuOpen(true)}
                                        className="w-full bg-gray-50 dark:bg-zinc-900 rounded-2xl p-4 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-zinc-800/70 transition-colors group"
                                    >
                                        {user ? (
                                            <div className="flex items-center gap-4 flex-1">
                                                <Avatar size="md" alt={user.name} src={user.avatar} />
                                                <div className="flex flex-col flex-1 min-w-0 text-left">
                                                    <span className="text-base font-semibold text-gray-900 dark:text-white leading-tight truncate">{user.name}</span>
                                                    <span className="text-xs text-gray-500 dark:text-zinc-400 font-normal truncate">{user.email || 'Conta Gratuita'}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className="w-10 h-10 rounded-full bg-white dark:bg-zinc-800 flex items-center justify-center">
                                                    <img src="/assets/hia-logo.png" alt="HIA" className="h-5 w-auto opacity-50" style={{ mixBlendMode: 'screen' }} />
                                                </div>
                                                <div className="flex flex-col flex-1 min-w-0 text-left">
                                                    <span className="text-base font-semibold text-gray-900 dark:text-white leading-tight">Bem-vindo</span>
                                                    <span className="text-xs text-gray-500 dark:text-zinc-400 font-normal">Faça login para continuar</span>
                                                </div>
                                            </div>
                                        )}

                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                onClose()
                                            }}
                                            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-zinc-700 transition-colors ml-2"
                                        >
                                            <X className="w-5 h-5 text-gray-500 dark:text-zinc-400" />
                                        </div>
                                    </button>
                                </div>

                                {/* Search Field */}
                                <div className="px-4 mb-6">
                                    <div className="relative group">
                                        {/* Gradient border wrapper */}
                                        <div className="rounded-2xl p-[1px] bg-gray-200 dark:bg-gray-800 group-focus-within:bg-gradient-to-br group-focus-within:from-blue-400/40 group-focus-within:via-purple-500/40 group-focus-within:to-pink-400/30 transition-all duration-200">
                                            <div className="relative bg-gray-50 dark:bg-zinc-900/50 rounded-2xl">
                                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 z-10" />
                                                <input
                                                    type="text"
                                                    placeholder="Pesquisar CtrlK"
                                                    className="w-full pl-11 pr-12 h-11 bg-transparent border-0 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-0 text-sm focus:outline-none"
                                                />
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-pink-500" />
                                                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Chat Section */}
                                <div className="px-4 mb-6">
                                    <p className="px-2 text-xs font-normal text-gray-500 dark:text-zinc-500 mb-2">Chat</p>
                                    <div className="bg-gray-50 dark:bg-zinc-900/50 rounded-2xl overflow-hidden">
                                        <MobileMenuItem
                                            label="Nova Conversa"
                                            description="Inicie um novo chat limpo"
                                            icon={MessageSquarePlus}
                                            onClick={() => {
                                                onChatAction?.('new')
                                                onClose()
                                            }}
                                            showChevron={false}
                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                            labelClassName="text-gray-900 dark:text-white"
                                            className="hover:bg-gray-100 dark:hover:bg-zinc-800/50"
                                        />
                                        <MobileMenuItem
                                            label="Histórico"
                                            description="Veja suas conversas anteriores"
                                            icon={History}
                                            onClick={() => {
                                                onChatAction?.('history')
                                                onClose()
                                            }}
                                            showChevron={false}
                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                            labelClassName="text-gray-900 dark:text-white"
                                            className="hover:bg-gray-100 dark:hover:bg-zinc-800/50"
                                        />
                                        <MobileMenuItem
                                            label="Configurações"
                                            description="Ajustes de IA e preferências"
                                            icon={Settings}
                                            onClick={() => {
                                                onChatAction?.('settings')
                                                onClose()
                                            }}
                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                            labelClassName="text-gray-900 dark:text-white"
                                            className="hover:bg-gray-100 dark:hover:bg-zinc-800/50"
                                        />
                                    </div>
                                </div>

                                {/* Navigation Section */}
                                <div className="px-4 mb-6">
                                    <p className="px-2 text-xs font-normal text-gray-500 dark:text-zinc-500 mb-2">Navegação</p>
                                    <div className="bg-gray-50 dark:bg-zinc-900/50 rounded-2xl overflow-hidden">
                                        <MobileMenuItem
                                            label="Agentes"
                                            description="Gerencie seus assistentes de IA"
                                            icon={AtSign}
                                            onClick={() => {
                                                window.location.href = '/agentes'
                                                onClose()
                                            }}
                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                            labelClassName="text-gray-900 dark:text-white"
                                            className="hover:bg-gray-100 dark:hover:bg-zinc-800/50"
                                        />
                                        <MobileMenuItem
                                            label="Lista"
                                            description="Visualização em lista de tarefas"
                                            icon={List}
                                            onClick={onClose}
                                            showChevron={false}
                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                            labelClassName="text-gray-900 dark:text-white"
                                            className="hover:bg-gray-100 dark:hover:bg-zinc-800/50"
                                        />
                                        <MobileMenuItem
                                            label="Quadro"
                                            description="Gerenciamento estilo Kanban"
                                            icon={Layout}
                                            onClick={onClose}
                                            showChevron={false}
                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                            labelClassName="text-gray-900 dark:text-white"
                                            className="hover:bg-gray-100 dark:hover:bg-zinc-800/50"
                                        />
                                        <MobileMenuItem
                                            label="Calendário"
                                            description="Agenda e compromissos"
                                            icon={Calendar}
                                            onClick={onClose}
                                            showChevron={false}
                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                            labelClassName="text-gray-900 dark:text-white"
                                            className="hover:bg-gray-100 dark:hover:bg-zinc-800/50"
                                        />

                                        {/* Visualization Expandable */}
                                        <button
                                            onClick={() => setIsVisualizationOpen(!isVisualizationOpen)}
                                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors text-left group"
                                        >
                                            <div className="flex items-center gap-4">
                                                <Plus className="w-6 h-6 shrink-0 text-gray-700 dark:text-zinc-400" />
                                                <div className="flex flex-col">
                                                    <span className="text-[15px] font-semibold text-gray-900 dark:text-white">Mais Visualizações</span>
                                                    <span className="text-xs text-gray-500 dark:text-zinc-500 font-normal mt-0.5">Gráficos, mapas e mais</span>
                                                </div>
                                            </div>
                                            {isVisualizationOpen ? (
                                                <ChevronDown className="w-5 h-5 text-gray-400" />
                                            ) : (
                                                <ChevronRight className="w-5 h-5 text-gray-400" />
                                            )}
                                        </button>

                                        {/* Visualization Submenu */}
                                        <AnimatePresence>
                                            {isVisualizationOpen && (
                                                <motion.div
                                                    {...({
                                                        initial: { height: 0, opacity: 0 },
                                                        animate: { height: "auto", opacity: 1 },
                                                        exit: { height: 0, opacity: 0 }
                                                    } as any)}
                                                    className="overflow-hidden bg-gray-100 dark:bg-zinc-800/30"
                                                >
                                                    <div className="py-2 space-y-1">
                                                        <MobileMenuItem
                                                            label="Gantt"
                                                            icon={BarChart2}
                                                            isSubmenuItem
                                                            onClick={onClose}
                                                            showChevron={false}
                                                            labelClassName="text-gray-900 dark:text-white"
                                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                                        />
                                                        <MobileMenuItem
                                                            label="Documento"
                                                            icon={FileText}
                                                            isSubmenuItem
                                                            onClick={onClose}
                                                            showChevron={false}
                                                            labelClassName="text-gray-900 dark:text-white"
                                                            iconClassName="text-gray-700 dark:text-zinc-400"
                                                        />
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                {/* Logout Button (ChatGPT Style) */}
                                {user && (
                                    <div className="px-4 mb-4">
                                        <button
                                            onClick={logout}
                                            className="w-full bg-gray-50 dark:bg-zinc-900/50 rounded-2xl px-4 py-4 flex items-center gap-4 hover:bg-gray-100 dark:hover:bg-zinc-800/50 transition-colors group"
                                        >
                                            <LogOut className="w-6 h-6 text-red-500" />
                                            <span className="text-[15px] font-semibold text-red-500">Sair</span>
                                        </button>
                                    </div>
                                )}

                                {/* Login/Signup Buttons (if not logged in) */}
                                {!user && (
                                    <div className="px-4 space-y-3">
                                        <Button
                                            onClick={() => window.location.href = '/login'}
                                            className="w-full bg-black dark:bg-white text-white dark:text-black hover:opacity-90 h-12 rounded-2xl font-semibold text-sm"
                                        >
                                            Entrar na conta
                                        </Button>
                                        <Button
                                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white h-12 rounded-2xl font-semibold text-sm shadow-lg shadow-blue-500/20 border-0"
                                        >
                                            Criar conta gratuita
                                        </Button>
                                    </div>
                                )}

                                {/* Safe Area Spacer for iOS Home Bar */}
                                <div className="h-8" />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* User Menu Component */}
            <UserMenu
                isOpen={isUserMenuOpen}
                onClose={() => setIsUserMenuOpen(false)}
            />
        </>
    )
}
